import type { TFunction } from 'i18next';
import { z } from 'zod';

import { DATE_RANGE_VALUES, type DateRangeParams } from '@/lib/date-range';
import { metricOfferedTo } from '@/components/reports/shared/report-filter';
import type { ReportWindowParams } from '@/repository/custom-reports';
import type { BusinessModel } from '@/types/generated';
import {
  DimensionId,
  FilterOperator,
  MetricAggregation,
  MetricId,
  TimeGrain,
} from '@/types/generated/enums';

const metricIds = Object.values(MetricId) as [MetricId, ...MetricId[]];
const dimensionIds = Object.values(DimensionId) as [
  DimensionId,
  ...DimensionId[],
];
const metricAggregations = Object.values(MetricAggregation) as [
  MetricAggregation,
  ...MetricAggregation[],
];
const filterOperators = Object.values(FilterOperator) as [
  FilterOperator,
  ...FilterOperator[],
];
const timeGrains = Object.values(TimeGrain) as [TimeGrain, ...TimeGrain[]];

// A URL-only guard; the server enforces the registry's own `max_rows`.
const URL_LIMIT_CAP = 1000;

const metricSchema = z.object({
  metric: z.enum(metricIds),
  aggregation: z.enum(metricAggregations),
});

const filterSchema = z.object({
  field: z.enum(dimensionIds),
  operator: z.enum(filterOperators),
  // A filter mid-build has no value yet; specProblems stops it reaching the server.
  values: z.array(z.string()),
});

export const customSpecSchema = z.object({
  metrics: z.array(metricSchema).min(1),
  date_range: z
    .object({
      preset: z.enum(DATE_RANGE_VALUES).default('LAST_30D'),
      start_date: z.string().nullable().optional(),
      end_date: z.string().nullable().optional(),
    })
    .default({ preset: 'LAST_30D' }),
  dimensions: z.array(z.enum(dimensionIds)).default([]),
  filters: z.array(filterSchema).default([]),
  group_by: z.enum(timeGrains).default('none'),
  sort: z
    .object({ field: z.string(), direction: z.enum(['asc', 'desc']) })
    .nullable()
    .default(null),
  limit: z.number().int().min(1).max(URL_LIMIT_CAP).default(100),
});

export type CustomSpec = z.infer<typeof customSpecSchema>;

export const EMPTY_SPEC: CustomSpec = {
  metrics: [{ metric: MetricId.SPEND, aggregation: MetricAggregation.SUM }],
  date_range: { preset: 'LAST_30D' },
  dimensions: [],
  filters: [],
  group_by: 'none',
  sort: null,
  limit: 100,
};

export interface RestoredSpec {
  spec: CustomSpec;
  // What a marketer reads as missing: each part of the stored spec that no longer parses.
  lost: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const keepParsing = <T>(
  raw: unknown,
  schema: z.ZodType<T>,
  describe: (item: unknown) => string,
  lost: string[],
): T[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const parsed = schema.safeParse(item);
    if (parsed.success) return [parsed.data];
    lost.push(describe(item));
    return [];
  });
};

const field = (item: unknown, key: string): unknown =>
  isRecord(item) ? item[key] : item;

const scalar = <K extends 'date_range' | 'group_by' | 'sort' | 'limit'>(
  stored: Record<string, unknown>,
  key: K,
  lostAs: string,
  lost: string[],
): CustomSpec[K] => {
  const parsed = customSpecSchema.shape[key].safeParse(stored[key]);
  if (parsed.success) return parsed.data as CustomSpec[K];
  lost.push(lostAs);
  return EMPTY_SPEC[key];
};

// A sort on a field the restore dropped would be refused, so it goes with that field.
const keptSort = (
  t: TFunction<'reports'>,
  sort: CustomSpec['sort'],
  metrics: CustomSpec['metrics'],
  dimensions: CustomSpec['dimensions'],
  lost: string[],
): CustomSpec['sort'] => {
  if (sort === null) return null;
  const fields = new Set<string>([
    ...metrics.map((m) => m.metric),
    ...dimensions,
  ]);
  if (fields.has(sort.field)) return sort;
  lost.push(t('custom_builder.restore_lost_sort_by', { value: sort.field }));
  return null;
};

// A saved spec can name a metric no longer offered; it runs without it, falling back to Spend.
export const specOfferedTo = (
  t: TFunction<'reports'>,
  spec: CustomSpec,
  businessModel: BusinessModel,
): CustomSpec => {
  const offered = spec.metrics.filter((m) =>
    metricOfferedTo(m.metric, businessModel),
  );
  if (offered.length === spec.metrics.length) return spec;
  const metrics = offered.length > 0 ? offered : EMPTY_SPEC.metrics;
  return {
    ...spec,
    metrics,
    sort: keptSort(t, spec.sort, metrics, spec.dimensions, []),
  };
};

// A stored spec restored part by part, so one retired field costs that field, not the whole report.
export const restoreStoredSpec = (
  t: TFunction<'reports'>,
  value: unknown,
): RestoredSpec => {
  const whole = customSpecSchema.safeParse(value);
  if (whole.success) return { spec: whole.data, lost: [] };
  if (!isRecord(value)) {
    return {
      spec: EMPTY_SPEC,
      lost: [t('custom_builder.restore_lost_whole_report')],
    };
  }

  const lost: string[] = [];
  const metrics = keepParsing(
    value.metrics,
    metricSchema,
    (item) =>
      t('custom_builder.restore_lost_metric', { value: field(item, 'metric') }),
    lost,
  );
  if (metrics.length === 0) {
    lost.push(t('custom_builder.restore_lost_metrics_fallback'));
  }
  const dimensions = keepParsing(
    value.dimensions,
    z.enum(dimensionIds),
    (item) => t('custom_builder.restore_lost_breakdown', { value: item }),
    lost,
  );
  const filters = keepParsing(
    value.filters,
    filterSchema,
    (item) =>
      t('custom_builder.restore_lost_filter_on', {
        value: field(item, 'field'),
      }),
    lost,
  );
  const kept = metrics.length > 0 ? metrics : EMPTY_SPEC.metrics;
  const spec: CustomSpec = {
    metrics: kept,
    dimensions,
    filters,
    date_range: scalar(
      value,
      'date_range',
      t('custom_builder.restore_lost_date_range'),
      lost,
    ),
    group_by: scalar(
      value,
      'group_by',
      t('custom_builder.restore_lost_grouping', { value: value.group_by }),
      lost,
    ),
    sort: keptSort(
      t,
      scalar(value, 'sort', t('custom_builder.restore_lost_sort'), lost),
      kept,
      dimensions,
      lost,
    ),
    limit: scalar(
      value,
      'limit',
      t('custom_builder.restore_lost_row_limit'),
      lost,
    ),
  };
  return { spec, lost };
};

// The window query a spec's date range sends to a windowed custom-report read.
export const specWindowParams = (
  dateRange: CustomSpec['date_range'],
): ReportWindowParams => ({
  date_range: dateRange.preset,
  start_date: dateRange.start_date ?? undefined,
  end_date: dateRange.end_date ?? undefined,
});

// The spec over a period chosen elsewhere; the inverse of specWindowParams.
export const withPeriod = (
  spec: CustomSpec,
  period: DateRangeParams,
): CustomSpec => ({
  ...spec,
  date_range: {
    preset: period.date_range,
    start_date: period.start_date,
    end_date: period.end_date,
  },
});
